import React, { Children, Component } from 'react';
import PropTypes from 'prop-types';
import Dots from './dots';
import SmallImgIndex from './SmallImgIndex';
import ArrowBtn from './ArrowBtn';
import { getElementLeft } from '../util/xyPosition';
import easyToMap from '../util/easyToMap';

class Carousel extends Component {
  /** *
   * params:
   *  type: 轮播图切换类型。
   *  dots: 底部显示跳转点的类型：目前存在 dots(点) smallImg(缩略图片) 若为空则不显示。
   *  time: number类型，划过一张幻灯片的时间。
   *
   * */
  static propTypes = {
    /**
     * 轮播组件的宽度
     * */
    width: PropTypes.number,
    /**
     * 轮播组件的高度
     * */
    height: PropTypes.number,
    /**
     * @ignore
     * */
    imgWidth: PropTypes.number,
    /**
     * @ignore
     * */
    imgHeight: PropTypes.number,
    /**
     * 自动切换轮播的时间
     * */
    interval: PropTypes.number,
    /**
     * 是否自动播放
     * */
    autoPlay: PropTypes.bool,
    /**
     * dot显示的方式：
     * display(一直显示),
     * hover(hover时候显示),
     * hidden(一直隐藏)
     * */
    dotsDisplayType: PropTypes.oneOf(['display', 'hover', 'hidden']),
    /**
     * pre next按钮显示的方式：
     * display(一直显示),
     * hover(hover时候显示),
     * hidden(一直隐藏)
     * */
    arrowDisplayType: PropTypes.oneOf(['display', 'hover', 'hidden']),
    /**
     * 类名前缀
     * */
    prefixCls: PropTypes.string,
    /**
     * @ignore
     * */
    switchType: PropTypes.string,
    /**
     * @ignore
     * */
    smallImgWidth: PropTypes.number,
    /**
     * @ignore
     * */
    imgGap: PropTypes.number,
    /**
     * @ignore
     * */
    activeIndex: PropTypes.number,
    /**
     * @ignore
     * */
    imgList: PropTypes.array,
    /**
     * 轮播组件渲染的自定义子元素
     * */
    children: PropTypes.node,
    /**
     * 子元素过渡的时间
     * */
    time: PropTypes.number,
  };

  static defaultProps = {
    prefixCls: 'ne-carousel',
    imgList: [],
    time: 0.6,
    interval: 3000, // 切换间隔时间
    autoPlay: false, // 是否自动播放
    switchType: 'custom', // 切换轮播的类型
    dotsDisplayType: 'hover', // 底部圆点显示的类型
    arrowDisplayType: 'hover', // 左右箭头切换的类型
    smallImgWidth: 64, // 类型为glimmer时候的小图尺寸
    imgGap: 8, // 类型为glimmer时候的图片间隔
    activeIndex: 1, // 当前活动的图片
  };

  constructor(props) {
    super(props);
    this.index = 1;
    this.animated = false;
    const { imgWidth, imgHeight } = this.props;
    const { width = imgWidth, height = imgHeight, children = [], autoPlay, time } = this.props;
    this.state = {
      activeIndex: 1, // 当前显示的图片
      len: 0,
      autoPlay,
      arrowDisplay: false,
      customAnimationCss: Object.assign(
        {
          width: (children.length + 2) * width,
          height,
        },
        ...this.prefixBroswerHack({ transform: `translate3d(${-width}px, 0px, 0px)` }),
        ...this.prefixBroswerHack({ transition: `transform ${time}s ease-in-out 0s` }),
      ),
    };
  }

  componentDidMount() {
    const { imgList, switchType, children, autoPlay } = this.props;
    if (switchType === 'custom') {
      this.carousel.addEventListener('mouseenter', this.handleMouseListener, false);
      this.carousel.addEventListener('mouseleave', this.handleMouseListener, false);
      this.customInner.addEventListener('transitionend', this.stopTransition, false);
    }
    this.setState({
      len: switchType === 'custom' ? children.length : imgList.length,
      activeIndex: this.props.activeIndex,
    });
    switchType === 'glimmer' && this.mouseOver();
    autoPlay && this.play();
  }

  componentWillReceiveProps() {
    if (!this.state.autoPlay) {
      const { imgList, autoPlay, switchType, children } = this.props;
      this.setState(
        {
          len: switchType === 'custom' ? children.length : imgList.length,
          autoPlay,
        },
        () => {
          this.state.autoPlay && this.play();
        },
      );
    }
  }

  componentWillUnmount() {
    if (this.state.autoPlay) {
      this.stop();
      clearTimeout(this.timer);
    }
    this.carousel.removeEventListener('mouseenter', this.handleMouseListener, false);
    this.carousel.removeEventListener('mouseleave', this.handleMouseListener, false);
    if (this.props.switchType === 'glimmer') {
      this.container.removeEventListener('mousemove', this.touchAreaToSlide);
    }
  }

  // 箭头点击
  arrowClick = (width, nextBool) => {
    const { switchType } = this.props;
    // 图片正在移动，则返回
    if (this.animated) {
      return;
    }
    // 图片目前已经到最后一张,则移动到第一张
    let targetIndex;
    const { len, activeIndex } = this.state;
    if (nextBool) {
      if (activeIndex === len) {
        targetIndex = 1;
      } else {
        targetIndex = activeIndex + 1;
      }
    } else if (activeIndex === 1) {
      targetIndex = len;
    } else {
      targetIndex = activeIndex - 1;
    }
    this.setState({
      activeIndex: targetIndex,
      next: nextBool,
    });
    if (switchType === 'custom') {
      this.customAnimation(nextBool);
    }
    this.customAnimation(nextBool);
    this.imgDotsSlide(targetIndex);
  };

  // 轮播dots
  imgDotsSlide = currentIndex => {
    const { imgWidth } = this.props;
    const { smallImgWidth, width = imgWidth, imgGap } = this.props;
    const len = this.state.len;
    this.myIndex = parseInt(currentIndex, 10); // 得到需要移动到的位置
    const distance = ((smallImgWidth + imgGap) * len - width) / len; // 单位距离
    let transformX = distance * (0 - this.myIndex);
    if (this.myIndex <= 3) {
      transformX = 0;
    } else if (this.myIndex >= len - 3) {
      transformX = -distance * len;
    }
    if (len * smallImgWidth + (len - 1) * imgGap <= width) {
      this.setState({
        activeIndex: this.myIndex,
      });
      return;
    }
    this.setState({
      smallIndexLeft: transformX,
      activeIndex: this.myIndex,
    });
  };

  // 根据类型判断是否显示切换按钮
  handleMouseListener = event => {
    const { dotsDisplayType, arrowDisplayType } = this.props;
    const typeOfSlideBtn = (type, prevState) => {
      let btnState = '';
      switch (type) {
        case 'hover':
          btnState = !prevState;
          break;
        case 'display':
          btnState = true;
          break;
        case 'hidden':
          btnState = false;
          break;
        default:
          return;
      }
      return btnState;
    };
    const arrowDisplay = typeOfSlideBtn(arrowDisplayType, this.state.arrowDisplay);
    const displayDots = typeOfSlideBtn(dotsDisplayType, this.state.displayDots);
    this.setState({
      arrowDisplay,
      displayDots,
    });
    if (event.type === 'mouseenter') {
      this.stop();
    }
    if (event.type === 'mouseleave') {
      this.play();
    }
  };

  mouseOver = () => {
    const { switchType } = this.props;
    if (switchType === 'glimmer') {
      this.container.addEventListener('mousemove', this.touchAreaToSlide, false);
    }
  };

  // 得到鼠标的位置
  getMousePos = event => {
    const e = event || window.event;
    const scrollX = document.documentElement.scrollLeft || document.body.scrollLeft;
    const scrollY = document.documentElement.scrollTop || document.body.scrollTop;
    const x = e.pageX || e.clientX + scrollX;
    const y = e.pageY || e.clientY + scrollY;
    return { x, y };
  };

  // glimmer点击的区域
  touchAreaToSlide = e => {
    // 获取当前鼠标点击的横坐标
    const x = this.getMousePos(e).x;
    // 减去父容器的横坐标位置，得到点击位置相对于父容器的区域
    const offsetX = x - getElementLeft(this.container);
    switch (this.onJudgeArea(offsetX)) {
      case 'left':
        if (this.state.activeIndex !== 1) {
          this.container.style.cursor =
            'url("http://static.ws.126.net/utf8/bjzw/img/leftArrow.png") 16 30, auto';
        } else {
          this.container.style.cursor = 'default';
        }
        break;
      case 'center':
        this.container.style.cursor = 'zoom-out';
        break;
      case 'right':
        if (this.state.activeIndex !== this.props.imgList.length) {
          this.container.style.cursor =
            'url("http://static.ws.126.net/utf8/bjzw/img/rightArrow.png") 16 30, auto';
        } else {
          this.container.style.cursor = 'default';
        }
        break;
      default:
    }
  };

  play = () => {
    const { imgWidth } = this.props;
    const { interval = 3000, width = imgWidth } = this.props;
    this.timer = setTimeout(() => {
      this.arrowClick(width, true);
      this.play();
    }, interval);
  };

  stop = () => {
    clearTimeout(this.timer);
  };

  dotClick = (currentIndex, type) => {
    const { switchType = 'custom' } = this.props;
    if (this.animated) {
      return;
    }
    this.myIndex = parseInt(currentIndex, 10); // 得到需要移动到的位置
    const next = this.myIndex - this.state.activeIndex > 0;
    switch (switchType) {
      // case 'slide':
      //     const offset = -imgWidth * (this.myIndex - this.state.activeIndex) // 判断出需要偏移的位移量
      //     this.animate(offset, next) // 去移动
      //     break
      case 'glimmer':
        break;
      case 'custom':
        this.customAnimation(next, true, currentIndex);
        break;
      default:
        return;
    }
    this.imgDotsSlide(this.myIndex);
    this.scrollToRightPosition(this.carousel);
  };

  // glimmer的点击触发
  handleGlimmerClick = (e, item) => {
    const { onClick } = this.props;
    const x = this.getMousePos(e).x;
    const offsetX = x - getElementLeft(this.container);
    switch (this.onJudgeArea(offsetX)) {
      case 'left':
        if (this.state.activeIndex !== 1) {
          this.dotClick(this.state.activeIndex - 1);
        }
        break;
      case 'center':
        onClick(item);
        break;
      case 'right':
        if (this.state.activeIndex !== this.props.imgList.length) {
          this.dotClick(this.state.activeIndex + 1);
        }
        break;
      default:
        return;
    }
    this.scrollToRightPosition(this.carousel);
  };

  // 跳转到正确的位置
  scrollToRightPosition = element => {
    const y =
      this.getElementTop(element) - document.documentElement.scrollTop || document.body.scrollTop;
    if (y < 0) {
      const { pageXOffset, scrollTo } = window;
      scrollTo(pageXOffset, this.getElementTop(this.carousel));
    }
  };

  // 得到目标元素的顶部
  getElementTop = element => {
    let actualTop = element.offsetTop;
    let current = element.offsetParent;
    while (current !== null) {
      actualTop += current.offsetTop;
      current = current.offsetParent;
    }
    return actualTop;
  };

  // 判断区域
  onJudgeArea = offsetX => {
    const { imgWidth } = this.props;
    const { width = imgWidth } = this.props;
    if (offsetX >= 0 && offsetX <= width / 4) {
      return 'left';
    }
    if (offsetX >= width / 4 && offsetX <= (width / 4) * 3) {
      return 'center';
    }
    if (offsetX >= (width / 4) * 3) {
      return 'right';
    }
  };

  // 为js里面的css加各个浏览器的前缀
  prefixBroswerHack = jsStyle => {
    const allStyle = [jsStyle];
    const prefixBroswer = ['Webkit', 'ms', 'O'];
    prefixBroswer.forEach(item => {
      allStyle.push({
        [item +
        Object.keys(jsStyle)[0].replace(/( |^)[a-z]/g, L => L.toUpperCase())]: Object.values(
          jsStyle,
        )[0],
      });
    });
    return allStyle;
  };

  // 动画开启
  customAnimation = (next, isJump, currentIndex) => {
    const { imgWidth, imgHeight } = this.props;
    const { width = imgWidth, height = imgHeight, time } = this.props;
    const { activeIndex, len } = this.state;
    const customInnerStyle = {
      width: (len + 2) * width,
      height,
    };
    let animationTarget = activeIndex;
    this.animated = true;
    if (activeIndex === 1) {
      if (next) {
        // 向右滚动到最后一幅
        animationTarget = len + 1;
      }
    } else if (activeIndex === len) {
      if (!next) {
        animationTarget = 0;
      }
    }
    if (isJump) {
      animationTarget = currentIndex;
    }
    Object.assign(
      customInnerStyle,
      ...this.prefixBroswerHack({
        transform: `translate3d(${-animationTarget * width}px, 0px, 0px)`,
      }),
      ...this.prefixBroswerHack({ transition: `transform ${time}s ease-in-out 0s` }),
    );
    this.setState({
      customAnimationCss: customInnerStyle,
    });
  };

  // 滑动停止时候的触发事件
  stopTransition = () => {
    const { imgWidth, imgHeight } = this.props;
    const { width = imgWidth, height = imgHeight } = this.props;
    const { activeIndex, len, next } = this.state;
    this.animated = false;
    if ((activeIndex === 1 && next) || (activeIndex === len && !next)) {
      const customInnerStyle = {
        width: (len + 2) * width,
        height,
      };
      Object.assign(
        customInnerStyle,
        ...this.prefixBroswerHack({
          transform: `translate3d(${-activeIndex * width}px, 0px, 0px)`,
        }),
        ...this.prefixBroswerHack({ transition: `transform 0s ease-in-out 0s` }),
      );
      this.setState({
        customAnimationCss: customInnerStyle,
      });
    }
  };

  render() {
    const { imgWidth, imgHeight } = this.props;
    const {
      prefixCls,
      imgList,
      width = imgWidth,
      height = imgHeight,
      orientation,
      switchType,
      imgIndexDisplay,
      arrowDisplayType,
      children,
    } = this.props;
    const {
      arrowDisplay,
      activeIndex,
      displayDots,
      smallIndexLeft,
      len,
      customAnimationCss,
    } = this.state;
    const arrowStyle = {
      display: 'none',
    };
    if (arrowDisplay) {
      arrowStyle.display = 'block';
    }
    let smallImgList = this.props.smallImgList || imgList;
    smallImgList = smallImgList.length ? smallImgList : children ? easyToMap(children.length) : [];
    const DotsPorps = {
      smallImgList,
      len,
      orientation,
      activeIndex,
      handleClick: this.dotClick,
      display: displayDots,
    };
    let smallImgIndexProps = {};
    let warpCarouselStyle = {};
    let carouselStyle = {};
    let containerStyle = {};
    let displayArrow = false;
    let childrenLen;
    switch (switchType) {
      case 'glimmer':
        carouselStyle = {
          width: `${width}px`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        };
        containerStyle = {
          width: `${width}px`,
          overflow: 'initial',
          borderRadius: 0,
        };
        smallImgIndexProps = {
          smallImgList,
          len,
          orientation,
          activeIndex,
          handleClick: this.dotClick,
          display: imgIndexDisplay,
          left: smallIndexLeft,
          imgContainerWidth: width,
        };
        break;
      case 'custom':
        warpCarouselStyle = {
          height: imgIndexDisplay ? `${height + 65}px` : `${height}px`,
        };
        carouselStyle = {
          width: `${width}px`,
          height: `${height}px`,
        };
        containerStyle = {
          width: `${width}px`,
          height: `${height}px`,
        };
        displayArrow = len !== 1 && arrowDisplayType !== 'hidden';
        childrenLen = children.length;
        break;
      default:
        break;
    }
    return (
      <div className={prefixCls} style={warpCarouselStyle}>
        <div
          className={`${prefixCls}-warp`}
          ref={carousel => (this.carousel = carousel)}
          style={carouselStyle}>
          <div
            className="container"
            ref={container => (this.container = container)}
            style={containerStyle}>
            {switchType === 'glimmer' && (
              <div className="container-glimmer" style={{ width: '640px', overflow: 'hidden' }}>
                <div
                  className="container-glimmer-li"
                  onClick={e => this.handleGlimmerClick(e, imgList[activeIndex - 1])}>
                  <img src={imgList[activeIndex - 1].img} alt={imgList[activeIndex - 1].alt} />
                </div>
              </div>
            )}
            {switchType === 'custom' && (
              <div className="container-custom" style={carouselStyle}>
                <div
                  className="container-custom-inner"
                  ref={e => {
                    this.customInner = e;
                  }}
                  style={customAnimationCss}>
                  {len &&
                    React.cloneElement(children[childrenLen - 1], {
                      className: `${children[childrenLen - 1].props.className} custom-slide-slick`,
                      style: { width, height },
                    })}
                  {Children.map(children, item => {
                    const reactCloneElement = React.cloneElement(item, {
                      className: `${children[0].props.className} custom-slide-slick`,
                      style: { width, height },
                    });
                    return reactCloneElement;
                  })}
                  {len &&
                    React.cloneElement(children[0], {
                      className: `${children[0].props.className} custom-slide-slick`,
                      style: { width, height },
                    })}
                </div>
              </div>
            )}
            {switchType === 'custom' && <Dots {...DotsPorps} />}
          </div>
          {displayArrow && (
            <ArrowBtn
              arrowDisplay={arrowDisplay}
              direction="left-arrow"
              arrowClick={this.arrowClick}
              imgWidth={width}/>
          )}
          {displayArrow && (
            <ArrowBtn
              arrowDisplay={arrowDisplay}
              direction="right-arrow"
              arrowClick={this.arrowClick}
              imgWidth={width}/>
          )}
          <SmallImgIndex {...smallImgIndexProps} />
        </div>
      </div>
    );
  }
}

export default Carousel;



// WEBPACK FOOTER //
// ./src/carousel/Carousel.jsx